import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { CatalogThinker } from "~/api/aiAdminTypes";
import { useCreateThinker, useUpdatePersona } from "~/api/queries";
import { Select, TextArea } from "~/features/settings/controls";
import { FacetsEditor } from "~/features/settings/sections/thinkers/FacetsEditor";
import {
  panelId,
  type ThinkerPanel,
  ThinkerTabs,
  tabId,
} from "~/features/settings/sections/thinkers/ThinkerTabs";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Input } from "~/foundation/components/Input";
import { Text } from "~/foundation/components/Text";

/**
 * Author a persona, or edit one the workspace already has.
 *
 * The two halves talk to different endpoints on purpose. Creating posts to `thinkers`, which
 * mints the record AND its tenancy row — created here means available here, which is exactly
 * the "create did nothing" bug the server closed. Editing puts to `thinkers/personas/{id}`,
 * which INHERITS every field the form did not set: a one-field edit of a persona this
 * workspace has not claimed copies the rest of the record across rather than blanking it. That
 * is why this form sends only what changed.
 */
export function PersonaDialog({
  persona,
  onClose,
  initialPanel = null,
}: {
  /** null = create a new persona. */
  persona: CatalogThinker | null;
  onClose: () => void;
  /** The tab to open on (`?panel=`); only a stored persona has tabs beyond Model. */
  initialPanel?: ThinkerPanel | null;
}) {
  const { t } = useTranslation();
  const create = useCreateThinker();
  const update = useUpdatePersona();
  // The dialog's tabs; remembered while it is open. A new persona has only Model.
  const [panel, setPanel] = useState<ThinkerPanel>(
    persona && initialPanel ? initialPanel : "model",
  );

  const [name, setName] = useState(persona?.name ?? "");
  const [description, setDescription] = useState(persona?.description ?? "");
  const [personalityPrompt, setPersonalityPrompt] = useState(persona?.personalityPrompt ?? "");
  const [category, setCategory] = useState(persona?.category ?? "");
  const [temperature, setTemperature] = useState(
    persona?.temperature === null || persona?.temperature === undefined
      ? ""
      : String(persona.temperature),
  );
  const [maxOutputTokens, setMaxOutputTokens] = useState(
    persona?.maxOutputTokens === null || persona?.maxOutputTokens === undefined
      ? ""
      : String(persona.maxOutputTokens),
  );
  const [isAi, setIsAi] = useState(persona?.isAi ?? true);

  const editing = persona !== null;
  const busy = create.isPending || update.isPending;
  const valid = name.trim().length > 0;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!valid) {
      return;
    }

    if (!editing) {
      create.mutate(
        {
          name: name.trim(),
          description: description.trim(),
          isAi,
          ...(personalityPrompt.trim() ? { personalityPrompt: personalityPrompt.trim() } : {}),
          ...(category.trim() ? { category: category.trim() } : {}),
        },
        {
          onSuccess: () => {
            toast.success(t("Thinker created"));
            onClose();
          },
        },
      );
      return;
    }

    // Only what the form actually changed goes on the wire — an omitted field is inherited.
    const patch = {
      ...(name.trim() !== persona.name ? { displayName: name.trim() } : {}),
      ...(description.trim() !== persona.description ? { description: description.trim() } : {}),
      ...(personalityPrompt.trim() !== (persona.personalityPrompt ?? "")
        ? { personalityPrompt: personalityPrompt.trim() }
        : {}),
      ...(category.trim() !== (persona.category ?? "") ? { category: category.trim() } : {}),
      ...(temperature.trim() && Number.parseFloat(temperature) !== persona.temperature
        ? { temperature: Number.parseFloat(temperature) }
        : {}),
      ...(maxOutputTokens.trim() && Number.parseInt(maxOutputTokens, 10) !== persona.maxOutputTokens
        ? { maxOutputTokens: Number.parseInt(maxOutputTokens, 10) }
        : {}),
      ...(isAi !== persona.isAi ? { isAi } : {}),
    };

    update.mutate(
      { id: persona.id, patch },
      {
        onSuccess: () => {
          toast.success(t("Thinker updated"));
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={editing ? t("Edit thinker") : t("New thinker")}
      closeLabel={t("Close")}
      size="sheet"
      footer={
        panel === "model" ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button variant="primary" disabled={busy || !valid} onClick={submit}>
              {editing ? t("Save changes") : t("Create thinker")}
            </Button>
          </>
        ) : undefined
      }
    >
      <ThinkerTabs value={panel} onChange={setPanel} saved={editing} />
      <form
        onSubmit={submit}
        role="tabpanel"
        id={panelId("model")}
        aria-labelledby={tabId("model")}
        hidden={panel !== "model"}
        className="flex flex-col gap-3 pt-3"
      >
        <Input
          label={t("Name")}
          value={name}
          fullWidth
          onChange={(event) => setName(event.target.value)}
        />
        <Input
          label={t("Description")}
          value={description}
          fullWidth
          placeholder={t("What is this thinker for?")}
          onChange={(event) => setDescription(event.target.value)}
        />
        <TextArea
          label={t("Personality prompt")}
          value={personalityPrompt}
          rows={4}
          placeholder={t("Instructions this thinker always works under…")}
          hint={t("Left blank, the description is used — a thinker with no script is nobody.")}
          onChange={(event) => setPersonalityPrompt(event.target.value)}
        />
        <div className="grid gap-3 md:grid-cols-2">
          <Input
            label={t("Category")}
            value={category}
            fullWidth
            placeholder={t("e.g. Research")}
            onChange={(event) => setCategory(event.target.value)}
          />
          <Select
            label={t("Kind")}
            value={isAi ? "ai" : "human"}
            fullWidth
            onChange={(event) => setIsAi(event.target.value === "ai")}
          >
            <option value="ai">{t("AI")}</option>
            <option value="human">{t("Human role")}</option>
          </Select>
        </div>

        {editing ? (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <Input
                label={t("Temperature")}
                type="number"
                step="0.1"
                min="0"
                max="2"
                value={temperature}
                fullWidth
                placeholder={t("Server default")}
                onChange={(event) => setTemperature(event.target.value)}
              />
              <Input
                label={t("Max output tokens")}
                type="number"
                min="1"
                value={maxOutputTokens}
                fullWidth
                placeholder={t("Server default")}
                onChange={(event) => setMaxOutputTokens(event.target.value)}
              />
            </div>
            <Text variant="small">
              {persona.owned
                ? t("This workspace owns this record.")
                : t(
                    "Editing claims this thinker for your workspace — updates elsewhere stop reaching it.",
                  )}
            </Text>
          </>
        ) : null}
      </form>
      {/* Saves on its own: the facets route replaces the whole list, this form inherits. The
          other tabs, all mounted so a draft survives a tab switch. */}
      {editing ? (
        <FacetsEditor kind="persona" id={persona.id} name={persona.name} panel={panel} />
      ) : null}
    </Dialog>
  );
}
